import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const A4_WIDTH = 592.28;
const A4_HEIGHT = 841.89;

jsPDF.API.output2 = function (outputType = 'save', filename = 'document.pdf') {
    let result = null;
    switch (outputType) {
        case 'file':
            result = new File([this.output('blob')], filename, {
                type: 'application/pdf',
                lastModified: Date.now(),
            });
            break;
        case 'save':
            result = this.save(filename);
            break;
        default:
            result = this.output(outputType);
    }
    
    return result;
};

jsPDF.API.addBlank = function (x, y, width, height) {
    this.setFillColor(255, 255, 255);
    this.rect(x, y, Math.ceil(width), Math.ceil(height), 'F');
};

jsPDF.API.toCanvas = async function (element, width) {
    const canvas = await html2canvas(element,{
       
        allowTaint: true,
        taintTest: false,
        scale: '5',
        dpi: '480',
        background: '#fff',
        onrendered: (canvas) => {},
        
    });
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const height = (width / canvasWidth) * canvasHeight;
    const canvasData = canvas.toDataURL('image/jpeg', 1.0);
    return { width, height, data: canvasData };
};

jsPDF.API.addHeader = async function (x, width, header) {
    if (!(header instanceof HTMLElement)) return;
    let __header;
    if (this.__header) {
        __header = this.__header;
    } else {
        __header = await this.toCanvas(header, width);
        this.__header = __header;
    }
    const { height, data } = __header;
    this.addImage(data, 'JPEG', x, 0, width, height);
};

jsPDF.API.addFooter = async function (x, width, footer) {
    if (!(footer instanceof HTMLElement)) return;
    let __footer;
    if (this.__footer) {
        __footer = this.__footer;
    } else {
        __footer = await this.toCanvas(footer, width);
        this.__footer = __footer;
    }
    const { height, data } = __footer;
    this.addImage(data, 'JPEG', x, A4_HEIGHT - height, width, height);
};

/**
 * 生成pdf(处理多页pdf截断问题)
 * @param {Object} param
 * @param {HTMLElement} param.element - 需要转换的dom根节点
 * @param {number} [param.contentWidth=550] - 一页pdf的内容宽度，0-592.28
 * @param {number} [param.contentHeight=800] - 一页pdf的内容高度，0-841.89
 * @param {string} [param.outputType='save'] - 生成pdf的数据类型，添加了'file'类型，其他支持的类型见http://raw.githack.com/MrRio/jsPDF/master/docs/jsPDF.html#output
 * @param {string} [param.filename='document.pdf'] - pdf文件名
 * @param {number} param.x - pdf页内容距页面左边的高度，默认居中显示，为(A4宽度 - contentWidth) / 2)
 * @param {number} param.y - pdf页内容距页面上边的高度，默认居中显示，为(A4高度 - contentHeight) / 2)
 * @param {HTMLElement} param.header - 页眉dom元素
 * @param {HTMLElement} param.footer - 页脚dom元素
 * @param {boolean} [param.headerOnlyFirst=true] - 是否只在第一页添加页眉
 * @param {boolean} [param.footerOnlyLast=true] - 是否只在最后一页添加页脚
 * @param {string} [param.mode='adaptive'] - 生成pdf的模式，支持'adaptive'、'fixed'，'adaptive'需给dom添加标识，'fixed'需固定布局。
 * @param {string} [param.itemName='item'] - 给dom添加元素标识的名字，'adaptive'模式需在dom中设置
 * @param {string} [param.groupName='group'] - 给dom添加组标识的名字，'adaptive'模式需在dom中设置
 * @returns {Promise} 根据outputType返回不同的数据类型
 */
async function outputPdf({
    element, contentWidth = 550, contentHeight = 800,
    outputType = 'save', filename = 'document.pdf', x, y,
    header, footer, headerOnlyFirst = true, footerOnlyLast = true,
    mode = 'adaptive', itemName = 'item', groupName = 'group',
}) {
    if (!(element instanceof HTMLElement)) {
        throw new Error('The root element must be HTMLElement.');
    }

    const pdf = new jsPDF({
        unit: 'pt',
        format: 'a4',
        orientation: 'p',
    });
    const { width, height, data } = await pdf.toCanvas(element, contentWidth);
    const baseX = x == null ? (A4_WIDTH - contentWidth) / 2 : x;
    const baseY = y == null ? (A4_HEIGHT - contentHeight) / 2 : y;
    async function addHeader(isFirst) {
        if (isFirst || !headerOnlyFirst) {
            await pdf.addHeader(baseX, contentWidth, header);
        }
    }
    async function addFooter(isLast) {
        if (isLast || !footerOnlyLast) {
            await pdf.addFooter(baseX, contentWidth, footer);
        }
    }
    function addImage(_x, _y) {
        pdf.addImage(data, 'JPEG', _x, _y, width, height);
    }

    const params = {
        element, contentWidth, contentHeight, itemName, groupName,
        pdf, baseX, baseY, width, height, addImage, addHeader, addFooter,
    };
    switch (mode) {
        case 'adaptive':
            await outputWithAdaptive(params);
            break;
        case 'fixed':
        default:
            await outputWithFixedSize(params);
    }
    
    return pdf.output2(outputType, filename);
}

async function outputWithFixedSize({
    pdf, baseX, baseY, height, addImage, addHeader, addFooter, contentHeight,
}) {
    const pageNum = Math.ceil(height / contentHeight); // 总页数
    const arr = Array.from({ length: pageNum }).map((_, i) => i);
    for await (const i of arr) {
        addImage(baseX, baseY - i * contentHeight);
        const isFirst = i === 0;
        const isLast = i === arr.length - 1;
        if (!isFirst) {
            // 用空白遮挡顶部需要隐藏的部分
            pdf.addBlank(0, 0, A4_WIDTH, baseY);
        }
        if (!isLast) {
            // 用空白遮挡底部需要隐藏的部分
            pdf.addBlank(0, baseY + contentHeight, A4_WIDTH, A4_HEIGHT - (baseY + contentHeight));
        }
        await addHeader(isFirst);
        await addFooter(isLast);
        if (!isLast) {
            pdf.addPage();
        }
    }
}

async function outputWithAdaptive({
    element, contentWidth, itemName, groupName,
    pdf, baseX, baseY, addImage, addHeader, addFooter, contentHeight,
}) {
    // 从根节点遍历dom，计算出每页应放置的内容高度以保证不被截断
    // async function splitElement(){
    const splitElement = ()=>{
        const res = [];
        let posArr = []
        let pos = 0,newNodes,lastAllH = 0;
        const elementWidth = element.offsetWidth;
        function updatePos(height) {
            if (pos + height <= contentHeight) {
                posArr.push(height)
                pos += height;
                return;
            }
           
            res.push(pos);
           
            pos = height + posArr[posArr.length - 1];
          
        }
        /**
         * 将偏移量Y的值与图表高度相加为变量y，偏移量Y为oriY，从下到上排序（当y相同时使用偏移量X判断，从左到右），
         * 取数组第一个元素（即y最大的元素）为变量oneArrFir，判断其他元素的y是否比oneArrFir的oriY小，
         * 当小的时候，替换oneArrFir为当前元素，
         * （假定图表父元素被分割成了各个区域，判断分割的依据为每个区域最大的y，每个区域最大的y会有明显的差距，
         * 规律为从下往上，前一个区域的最大y的oriY要比后一个区域的最大y要大，以此判断是否到了不同的区域）
         * trans:{
         *      x: 偏移量X,
         *      y: 偏移量Y + 该元素的高度offsetHeight,判断使用，
         *      oriY: 偏移量Y ,循环判断用
         *      height:该元素的高度offsetHeight,判断使用
         *      offsetHeight:偏移量Y + 该元素的高度offsetHeight，最后traversingNodes中使用，随着该区域的最大height的变化而变化
         * }
         */
        function offsetCheck(nodes){
            if (nodes.length === 0){
                resolve()
            }
            
            checkData(nodes)
        }
        function checkData(nodes){
                let oneArr = [], arr=[];
                for (let i in nodes) {
                    if (nodes[i].nodeType === 1 && !Array.from(nodes[i].classList).includes('el-loading-mask')) {
                        oneArr.push(nodes[i])
                    }
                }
                // 取出偏移量放入trans中
                oneArr = oneArr.map((val)=>{
                    
                    let valTransY = val.style.transform.split(',')[1]&&val.style.transform.split(',')[1].replace(/px\)/, '')-0, 
                        valTransX = val.style.transform.split(',')[0]&&val.style.transform.split(',')[0].split('(')[1].replace(/px/, '')-0;
                    val.trans = { 
                        x: valTransX, 
                        y: valTransY + val.offsetHeight, 
                        oriY: valTransY,
                        height:val.offsetHeight, 
                        offsetHeight: valTransY + val.offsetHeight,
                        hingOriY: valTransY
                    }
                    return val
                })
                // 根据偏移的x与y排序，从下到上，从左到右
                oneArr = SetSortPoint(oneArr)
                // 获取第一个，并存储
                let oneArrFir = oneArr[0]
                arr.push(oneArrFir)
                for(let i=0;i<oneArr.length;i++){
                    //当oneArrFir的offsetHeight比oneArr[i]的y大并且oneArrFir的highOriY比oneArr[i]的y小时，说明这个元素在oneArrFir的范围内，略过
                    if (oneArrFir.trans.offsetHeight > oneArr[i].trans.y && oneArrFir.trans.hingOriY< oneArr[i].trans.y) {
                        // 当oneArrFir的height比oneArr[i]的height小时，更新oneArrFir的highOriY为oneArr[i]的oriY
                        if (oneArrFir.trans.height < oneArr[i].trans.height) {
                            oneArrFir.trans.hingOriY = oneArr[i].trans.oriY
                            arr[arr.length - 1] = oneArrFir
                        }
                        // continue
                    } else if (oneArrFir.trans.oriY > oneArr[i].trans.y){
                        // 当第一个的oriY大于某个元素的y时，说明此片区域已循环完，更新第一个为当前元素，继续循环，并将当前元素存储
                        oneArrFir = oneArr[i]
                        arr.push(oneArrFir)
                    }
                }
                // 反转数组顺序
                // newNodes = JSON.parse(JSON.stringify(arr)).reverse()
                newNodes = Array.from(arr) .reverse()
            
        }
        // 根据坐标排序
        function SetSortPoint(arry) {
            var len = arry.length;
            for (var i = 0; i < len - 1; i++) {
                for (var j = 0; j < len - 1 - i; j++) {
                    if (SetSortRule(arry[j], arry[j + 1])) {
                        var tmp = arry[j];
                        arry[j] = arry[j + 1];
                        arry[j + 1] = tmp;
                    }
                }
            }
            return arry
        }
        function SetSortRule(p1, p2) {
            if (p1.trans.y < p2.trans.y) {
                return true;
            }
            else if (p1.trans.y == p2.trans.y) {
                return (p1.trans.x > p2.trans.x);
            }
            else {
                return false;
            }
        }
        function traversingNodes(nodes) {
            if (nodes.length === 0) return;
            nodes.forEach((one,index) => {
                if (one.nodeType !== 1) return;
                const { [itemName]: item, [groupName]: group } = one.dataset;
                if (item != null) {
                    //每个图表的高度加上与上一个图表之间的间距作为完整的图表高度,使用循环后的offsetHeight
                    // let offsetHeight  = one.trans.height,transY = one.trans.transY;
                    let offsetHeight = one.trans.offsetHeight
                    // 计算正真的高度加间隔
                    if(index!=0){
                        offsetHeight = offsetHeight - lastAllH
                    }
                    // newHeight = Number(transY) + Number(offsetHeight)
                    //前面的图表的高度（包含间隔）的和，用于计算下一个元素正真的高度加间隔    
                    lastAllH += offsetHeight
                    // dom高度转换成生成pdf的实际高度
                    // 代码不考虑dom定位、边距、边框等因素，需在dom里自行考虑，如将box-sizing设置为border-box
                   
                    updatePos(contentWidth / elementWidth * offsetHeight);
                } else if (group != null) {
                    traversingNodes(one.childNodes);
                }
            });
        }
        offsetCheck(element.childNodes)
        traversingNodes(newNodes);
        
        res.push(pos);
       
        return res;
    };
    // async function out(res){
    let elements = splitElement();
        
        let accumulationHeight = 0;
        let currentPage = 0;
        for await (const elementHeight of elements) {
            addImage(baseX, baseY - accumulationHeight);
            accumulationHeight += elementHeight;
            const isFirst = currentPage === 0;
            const isLast = currentPage === elements.length - 1;
            if (!isFirst) {
                pdf.addBlank(0, 0, A4_WIDTH, baseY);
            }
            if (!isLast) {
                pdf.addBlank(0, baseY + elementHeight, A4_WIDTH, A4_HEIGHT - (baseY + elementHeight));
            }
            await addHeader(isFirst);
            await addFooter(isLast);
            if (!isLast) {
                pdf.addPage();
            }
            currentPage++;
        }
        
    // }
    // splitElement().then((res)=>{
    //     out(res)
    // })
}

export default outputPdf;


// WEBPACK FOOTER //
// ./src/util/outputPdf.js